import {
	PogisDocument,
	Regulation,
	RegulatoryDocument,
	RegulatoryDocumentParagraph,
	Requirement,
} from 'types';
import React, {useCallback, useMemo} from 'react';
import {useState} from 'react';
import {
	CommandButtonCompat as CommandButton,
	LinkCompat as Link,
	LabelCompat as Label,
	SeparatorCompat as Separator,
	StackCompat as Stack,
	IconButtonCompat as IconButton,
	ShimmerCompat as Shimmer,
	SpinnerCompat as Spinner,
	useTheme,
	TextCompat as Text,
	Theme,
	mergeStyleSets,
	PanelCompat as Panel,
	PanelType,
	FontIconCompat as FontIcon,
	PrimaryButtonCompat as PrimaryButton,
	DefaultButtonCompat as DefaultButton,
	Position,
	IButtonProps,
	IPanelProps,
} from 'components/FluentUICompat';

import {
	ArrayValues,
	renderParagraph,
} from 'components/EntityList/ColumnRenderers';
import {TFunction, useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {useGetSearchMoreParagraphsTocLazyQuery} from '../../hooks';
import {v4 as uuidv4} from 'uuid';
import {
	ParagraphNode,
	useNestedParagraphs,
} from 'features/RegulatoryDocuments/utils';
import CheckboxTree from 'react-checkbox-tree';
import {
	getArray,
	getTableRow,
	HighlightedText,
	searchResultStyle,
	SearchResultTemplate,
} from './SearchResultCards';

interface DataStoreParagraph {
	[key: string]: RegulatoryDocumentParagraph;
}
type DeepSearchResultProps = {
	itemMeta: RegulatoryDocument;
	itemSearchedParagraphs: RegulatoryDocument;
};

type DeepSearchTOCProps = DeepSearchResultProps & {
	treeItems: ParagraphNode[];
	initialChecked: string[];
	initialExpanded: string[];
	checkedApplied: string[];
	setCheckedApplied: (checkedApplied: string[]) => void;
	expandedApplied: string[];
	setExpandedApplied: (expandedApplied: string[]) => void;
	dsParagraphs: DataStoreParagraph;
	setDSParagraphs: (dsParagraphs: DataStoreParagraph) => void;
};

const nsTranslation = 'features/regulatorydocuments';

export const SearchResultDeep: React.FC<DeepSearchResultProps> = ({
	itemMeta,
	itemSearchedParagraphs,
}) => {
	const {t} = useTranslation(nsTranslation, {
		keyPrefix: 'ParagraphsList',
	});

	const theme = useTheme();

	const classnames = paragraphClassNames(theme);

	const regDocMeta = itemMeta as RegulatoryDocument;

	const initialDSParagraphs = useMemo(() => {
		const dsParagraphsInitial: DataStoreParagraph = {};
		itemSearchedParagraphs.paragraphs.forEach(p => {
			dsParagraphsInitial[p.id] = p as RegulatoryDocumentParagraph;
		});
		return dsParagraphsInitial;
	}, [itemSearchedParagraphs]);

	const treeParagraphs = useMemo(
		() =>
			itemMeta.paragraphs.map(p => {
				return {...p, enumeration: p.enumeration === '' ? '.' : p.enumeration};
			}),
		[itemMeta],
	);

	const treeItems: ParagraphNode[] = useNestedParagraphs(
		treeParagraphs,
		treeParagraphs,
	);

	const {initialChecked, initialExpanded} = useMemo(() => {
		const initialChecked = itemSearchedParagraphs.paragraphs.map(p => p.id);
		const initialExpanded = checkTree(treeItems, initialChecked);
		return {initialChecked, initialExpanded};
	}, [itemSearchedParagraphs, treeItems]);

	const [dsParagraphs, setDSParagraphs] =
		useState<DataStoreParagraph>(initialDSParagraphs);

	const [checkedApplied, setCheckedApplied] = useState([...initialChecked]);
	const [expandedApplied, setExpandedApplied] = useState([...initialExpanded]);

	const infoSection = useMemo(() => {
		const compLink = (
			<Link href={`/regulatoryDocuments/${regDocMeta.id}`} target='_blank'>
				<Stack horizontal>
					<span style={{paddingTop: 5}}>
						{regDocMeta.regulation?.regulationNumber ?? ''}&nbsp;|&nbsp;
					</span>
					<Label>
						<HighlightedText text={regDocMeta.name} />
					</Label>
				</Stack>
				<span>{regDocMeta.regulation?.name ?? ''} </span>
			</Link>
		);

		const compParagraphs = checkedApplied
			.filter(x => dsParagraphs[x] !== undefined)
			.map(x => dsParagraphs[x])
			.map(p => (
				<Stack key={p.id} className={classnames.group}>
					<Stack.Item>{renderParagraph({...p, level: 0}, t, true)}</Stack.Item>
					<Stack.Item>
						<table>
							{getTableRow(t('Position'), p.position)}
							{getTableRow(t('Keywords'), getArray(p.keywords))}
							{getTableRow(t('Categories'), getArray(p.categories))}
							{getTableRow(t('DriveVariants'), getArray(p.driveVariants))}
						</table>
					</Stack.Item>
				</Stack>
			));

		return (
			<>
				{compLink}
				{compParagraphs}
			</>
		);
	}, [regDocMeta, checkedApplied, dsParagraphs]);

	const propsTOC = {
		itemMeta,
		itemSearchedParagraphs,
		treeItems,
		initialChecked,
		initialExpanded,
		checkedApplied,
		setCheckedApplied,
		expandedApplied,
		setExpandedApplied,
		dsParagraphs,
		setDSParagraphs,
	};
	const sideSection = <DeepSearchTOC {...propsTOC} />;
	const propsResult = {infoSection, sideSection};

	return <SearchResultTemplate {...propsResult} />;
};

export const DeepSearchTOC: React.FC<DeepSearchTOCProps> = props => {
	const {t: tTOC} = useTranslation(nsTranslation, {
		keyPrefix: 'ParagraphTableOfContents',
	});

	const {t: tFilter} = useTranslation('components/infinitylist');

	const {
		itemMeta,
		treeItems,
		initialChecked,
		initialExpanded,
		checkedApplied,
		setCheckedApplied,
		expandedApplied,
		setExpandedApplied,
		dsParagraphs,
		setDSParagraphs,
	} = props;

	const [getMoreParagraphs, {loading: loadingPara}] =
		useGetSearchMoreParagraphsTocLazyQuery();

	const [checked, setChecked] = useState(initialChecked);
	const [expanded, setExpanded] = useState(initialExpanded);

	const theme = useTheme();

	const [showTOC, setShowTOC] = useState(false);

	const regulatoryDocumentID = itemMeta.id;

	const checkboxTOC = useMemo(() => {
		const iconTOC = getIcons(tTOC, theme);

		const handleCheckingTOC = (checkedParagraphIds: string[]) => {
			setChecked(checkedParagraphIds);
		};
		const handleExpandTOC = (expanded: Array<string>) => setExpanded(expanded);

		const propsTOC = {
			showNodeIcon: false,
			showExpandAll: true,
			checkModel: 'all',
			nodes: treeItems,
			checked,
			expanded,
			onCheck: handleCheckingTOC,
			onExpand: handleExpandTOC,
			icons: iconTOC,
			noCascade: true,
		};

		return <CheckboxTree {...propsTOC} />;
	}, [checked, expanded, treeItems]);

	const panelComponent = useMemo(() => {
		const onRenderBody: IPanelProps['onRenderBody'] = props => {
			return (
				<div
					style={{
						flexGrow: 1,
						overflowY: 'auto',
						padding: '10px 20px 10px 20px',
					}}
				>
					{checkboxTOC}
				</div>
			);
		};

		const handleApplyFilters = async () => {
			const newDSParagraphs: DataStoreParagraph = {};

			const paraId = checked.filter(x => dsParagraphs[x] === undefined);

			if (paraId.length > 0) {
				const {data} = await getMoreParagraphs({
					variables: {paraId, regulatoryDocumentID},
				});

				(data?.searchMoreParagraphsTOC ?? []).forEach(p => {
					newDSParagraphs[p.id] = p as RegulatoryDocumentParagraph;
				});
			}

			const expandedNew = checkTree(treeItems, checked);

			setCheckedApplied([...checked]);
			setExpanded(expandedNew);
			setExpandedApplied([...expandedNew]);
			setDSParagraphs({...dsParagraphs, ...newDSParagraphs});
		};

		const handleRemoveFilters = () => {
			setChecked([...initialChecked]);
			setCheckedApplied([...initialChecked]);
			setExpanded([...initialExpanded]);
		};

		const renderPanelFooter = () => (
			<>
				<Stack horizontal tokens={{childrenGap: 10}}>
					<PrimaryButton
						text={tFilter('ApplyFilters')}
						onClick={handleApplyFilters}
					/>
					<DefaultButton
						text={tFilter('ClearFilters')}
						onClick={handleRemoveFilters}
					/>
				</Stack>

				{loadingPara && (
					<>
						<Separator />
						<Spinner />
					</>
				)}
			</>
		);

		const handleDismissPanel = () => setShowTOC(false);

		const propsPanel = {
			headerText: tTOC('TableOfContents'),
			isOpen: showTOC,
			isBlocking: false,
			isLightDismiss: true,
			type: PanelType.medium,
			onRenderFooterContent: renderPanelFooter,
			onDismiss: handleDismissPanel,
			onRenderBody,
		};

		return <Panel {...propsPanel}></Panel>;
	}, [
		checked,
		checkedApplied,
		expandedApplied,
		expanded,
		dsParagraphs,
		showTOC,
		checkboxTOC,
	]);

	const propsCommandButton: IButtonProps = useMemo(() => {
		const handleClickTOC = () => {
			setChecked([...checkedApplied]);
			setExpanded([...expandedApplied]);
			setShowTOC(true);
		};

		return {
			iconProps: {iconName: 'BulletedList2'},
			text: tTOC('TableOfContents'),
			style: {position: 'sticky', top: 15, zIndex: 1000},
			onClick: handleClickTOC,
		};
	}, [checkedApplied, expandedApplied]);

	return (
		<>
			<CommandButton {...propsCommandButton} />
			{panelComponent}
		</>
	);
};

const checkTree = (nodes: ParagraphNode[], checked: string[]): string[] => {
	const expanded: string[] = [];
	for (const node of nodes) {
		checkNodeAndChildren(node, checked, expanded);
	}
	return expanded;
};

const checkNodeAndChildren = (
	node: ParagraphNode,
	checked: string[],
	expanded: string[],
): boolean => {
	let boolChildFound = false;
	for (const child of node.children ?? []) {
		if (checkNodeAndChildren(child as ParagraphNode, checked, expanded)) {
			boolChildFound = true;
		}
	}

	const boolInclude = boolChildFound || checked.includes(node.id);

	if (boolInclude) {
		expanded.push(node.id);
	}

	return boolInclude;
};

const getIcons = (tTOC: TFunction, theme: Theme) => ({
	check: (
		<FontIcon
			iconName='CheckboxCompositeReversed'
			style={{color: theme.palette.themePrimary}}
		/>
	),
	uncheck: <FontIcon iconName='Checkbox' />,
	halfCheck: <FontIcon iconName='CheckboxComposite' />,
	expandClose: <FontIcon iconName='ChevronDown' />,
	expandOpen: <FontIcon iconName='ChevronUp' />,
	expandAll: (
		<FontIcon
			iconName='CaretDownSolid8'
			style={{color: theme.palette.themePrimary}}
			title={tTOC('ExpandAll')}
		/>
	),
	collapseAll: (
		<FontIcon
			iconName='CaretUpSolid8'
			style={{color: theme.palette.themePrimary}}
			title={tTOC('CollapseAll')}
		/>
	),
});

const paragraphClassNames = (theme: Theme) =>
	mergeStyleSets({
		group: {
			width: '45vw',
			paddingLeft: '10px',
			paddingRight: '10px',
			selectors: {
				'&:hover': {
					borderTop: '4px solid',
					borderBottom: '4px solid',
					borderColor: theme.palette.neutralLight,
				},
			},
		},
	});
